const BASE = 'v1';
const TALENTCHEK_BASE = `${BASE}/talentchek`;

export const ENDPOINTS = {
  // ── Auth ──────────────────────────────────────────────────────────────────
  AUTH: {
    LOGIN: `/v2/users/login`,
    REGISTER: `/v2/users/signup`,
    LOGOUT: `/v2/users/logout`,
    REFRESH: `/v2/users/refresh`,
    FORGOT_PASSWORD: `/v2/users/forgot-password`,
    RESET_PASSWORD: `/v2/users/passwords/reset`,
    ME: `/v2/users/me`,
    AUTHORITY: `/v2/users/authority`,
  },

  // ── Candidates (TalentChek) ────────────────────────────────────────────────
  // Collection actions live in `NOCO.CANDIDATES`.

  // ── Employees ─────────────────────────────────────────────────────────────
  EMPLOYEES: {
    BASE: `/${TALENTCHEK_BASE}/employees?status=ACTIVE`,
    BY_ID: (id: string) => `/${TALENTCHEK_BASE}/employees/${id}`,
    CUSTOM_FIELDS: `/${BASE}/employees/custom-fields`,
    DOCUMENTS: (id: string) => `/${BASE}/employees/${id}/documents`,
    AVATAR: (id: string) => `/${BASE}/employees/${id}/avatar`,
  },

  // ── Leave ─────────────────────────────────────────────────────────────────
  LEAVE: {
    REQUESTS: `/${BASE}/leave/requests`,
    REQUEST_BY_ID: (id: string) => `/${BASE}/leave/requests/${id}`,
    APPROVE: (id: string) => `/${BASE}/leave/requests/${id}/approve`,
    POLICIES: `/${BASE}/leave/policies`,
    POLICY_BY_ID: (id: string) => `/${BASE}/leave/policies/${id}`,
    BALANCES: `/${BASE}/leave/balances`,
    BALANCE_BY_EMPLOYEE: (empId: string) => `/${BASE}/leave/balances/${empId}`,
  },

  // ── Performance ───────────────────────────────────────────────────────────
  PERFORMANCE: {
    GOALS: `/${BASE}/performance/goals`,
    GOAL_BY_ID: (id: string) => `/${BASE}/performance/goals/${id}`,
    APPRAISALS: `/${BASE}/performance/appraisals`,
    APPRAISAL_BY_ID: (id: string) => `/${BASE}/performance/appraisals/${id}`,
    FEEDBACK: `/${BASE}/performance/feedback`,
    SELF_EVALUATION: `/${BASE}/performance/self-evaluation`,
  },

  // ── Documents ─────────────────────────────────────────────────────────────
  DOCUMENTS: {
    POLICIES: `/${BASE}/documents/policies`,
    POLICY_BY_ID: (id: string) => `/${BASE}/documents/policies/${id}`,
    ACKNOWLEDGE: (id: string) => `/${BASE}/documents/policies/${id}/acknowledge`,
    UPLOAD: `/${BASE}/documents/upload`,
  },

  // ── Onboarding ────────────────────────────────────────────────────────────
  ONBOARDING: {
    CANDIDATES: `/${BASE}/onboarding/candidates`,
    CANDIDATE_BY_ID: (id: string) => `/${BASE}/onboarding/candidates/${id}`,
    INVITE: `/${BASE}/onboarding/invite`,
    JOB_ROLES: `/${BASE}/onboarding/job-roles`,
    TASKS: (candidateId: string) => `/${BASE}/onboarding/candidates/${candidateId}/tasks`,
  },

  // ── Recruitment ───────────────────────────────────────────────────────────
  RECRUITMENT: {
    REQUISITIONS: `/${BASE}/recruitment/requisitions`,
    REQUISITION_BY_ID: (id: string) => `/${BASE}/recruitment/requisitions/${id}`,
    JOB_POSTINGS: `/${BASE}/recruitment/postings`,
  },

  // ── Offboarding ───────────────────────────────────────────────────────────
  OFFBOARDING: {
    BASE: `/${BASE}/offboarding`,
    BY_ID: (id: string) => `/${BASE}/offboarding/${id}`,
    CHECKLIST: (id: string) => `/${BASE}/offboarding/${id}/checklist`,
    EXIT_INTERVIEW: (id: string) => `/${BASE}/offboarding/${id}/exit-interview`,
  },

  // ── Surveys ───────────────────────────────────────────────────────────────
  SURVEYS: {
    BASE: `/${BASE}/surveys`,
    BY_ID: (id: string) => `/${BASE}/surveys/${id}`,
    RESULTS: (id: string) => `/${BASE}/surveys/${id}/results`,
    RESPOND: (id: string) => `/${BASE}/surveys/${id}/respond`,
  },

  // ── LMS ───────────────────────────────────────────────────────────────────
  LMS: {
    COURSES: `/${BASE}/lms/courses`,
    COURSE_BY_ID: (id: string) => `/${BASE}/lms/courses/${id}`,
    ENROLLMENTS: `/${BASE}/lms/enrollments`,
    MY_TRAINING: `/${BASE}/lms/my-training`,
    ASSIGN: `/${BASE}/lms/assign`,
  },

  // ── Reports ───────────────────────────────────────────────────────────────
  REPORTS: {
    TEMPLATES: `/${BASE}/reports/templates`,
    GENERATE: `/${BASE}/reports/generate`,
    HISTORY: `/${BASE}/reports/history`,
    DOWNLOAD: (id: string) => `/${BASE}/reports/${id}/download`,
  },

  // ── Roles ─────────────────────────────────────────────────────────────────
  ROLES: {
    BASE: `/${BASE}/roles`,
    BY_ID: (id: string) => `/${BASE}/roles/${id}`,
    PERMISSIONS: `/${BASE}/roles/permissions`,
    ASSIGN: `/${BASE}/roles/assign`,
  },
} as const;

// ── NocoBase actions (TalentChek API) ────────────────────────────────────────
// Single source of truth for the TalentChek NocoBase table actions used by
// `nocoAxios`. Keeps action names in one place instead of string literals
// scattered across feature API modules. A helper generates the standard CRUD
// action names for every collection.
const nocoActions = (collection: string) => ({
  LIST: `${collection}:list`,
  GET: `${collection}:get`,
  CREATE: `${collection}:create`,
  UPDATE: `${collection}:update`,
  DESTROY: `${collection}:destroy`,
});

export const NOCO = {
  CANDIDATES: nocoActions('candidates'),
  EMPLOYEES: nocoActions('employee'),
  APPRAISAL_CYCLES: nocoActions('appraisalcircle'),
  APPRAISAL: nocoActions('appraisal'),
  ACKNOWLEDGEMENTS: nocoActions('acknowledgement'),
  ATTACHMENTS: nocoActions('attachments'),
  DIVISIONS: nocoActions('division'),
  LEAVE: {
    REQUESTS: nocoActions('leaveRequests'),
    POLICIES: nocoActions('leavePolicies'),
    BALANCES: nocoActions('leaveBalance'),
  },
  DEPARTMENTS: nocoActions('departments'),
  DESIGNATION: nocoActions('designation'),
  SUB_DEPARTMENTS: nocoActions('subDepartment'),
  COST_CENTER: nocoActions('costCenter'),
  ORGANIZATION: nocoActions('organization'),
  POLICIES: nocoActions('policies'),
  ENTITIES: nocoActions('entity'),
  SALARY_ADVANCE: nocoActions('salaryadvance'),
  PUBLIC_HOLIDAYS: nocoActions('publicHolidays'),
} as const;

export type NocoAction = (typeof NOCO)[keyof typeof NOCO];
